import { useReducer, useState } from 'react';
import {
  SET_ITEMS, ADD_ITEM, REMOVE_ITEM,
} from './CONSTANTS';
import { sanitize } from '../Utilities/utils';

// Define the initial state for the accordion.
export const initialState = Object.freeze({
  openItems: [],
  open: false,
  collapseSiblings: true,
  onChange: () => {},
});

// Handle changes to the list of open items in the accordion.
const openItemsReducer = (state, action) => {
  const { payload, type } = action;
  const { item } = payload;
  const { openItems } = state;

  switch (type) {
    case ADD_ITEM:
      return {
        ...state,
        openItems: [...openItems, item],
      };
    case REMOVE_ITEM:
      return {
        ...state,
        openItems: openItems.filter((i) => i !== item),
      };
    case SET_ITEMS:
      return {
        ...state,
        openItems: Object.freeze(payload.openItems),
      };
    default:
      return state;
  }
};

// Reducer to manage registered items of the accordion
const registeredItemsReducer = (state, id) => {
  if (id) {
    return [...state, id];
  }
  return state;
};

// main custom hook for managing the accordion
const useAccordion = (defaultState = {}) => {
  const [state, dispatchState] = useReducer(
    openItemsReducer,
    sanitize(defaultState, initialState),
  );
  const [registeredItems, dispatchRegisteredItems] = useReducer(registeredItemsReducer, []);

  // Close an item with a given `id`
  const closeItem = (id) => {
    const { openItems } = state;

    // Check if the item is open and then dispatch an action to remove it
    if (openItems && openItems.includes(id)) {
      dispatchState({
        type: REMOVE_ITEM,
        payload: { item: id },
      });
    }
  };

  // Open an accordion item with a given `id`
  const openItem = (id) => {
    const { openItems } = state;

    // always add id to be open
    if (openItems && !openItems.includes(id)) {
      dispatchState({
        type: ADD_ITEM,
        payload: { item: id },
      });
    }
  };

  const toggleItem = (id) => {
    // toggle logic for single or multiple open
    const { openItems, collapseSiblings } = state;

    if (collapseSiblings) {
      // If collapsing siblings, set the openItems array to either [id] or an empty array
      dispatchState({
        type: SET_ITEMS,
        payload: { openItems: openItems.includes(id) ? [] : [id] },
      });
    } else if (openItems.includes(id)) {
      // If not collapsing siblings and the item is open, close it
      closeItem(id);
    } else {
      // otherwise, open it
      openItem(id);
    }
  };

  // register an accrdion item with a give ID. We do this so that we can gather all items in the
  // accordion and output the state when onChange fires
  const registerItem = (id) => {
    dispatchRegisteredItems(id);
  };

  const getAccordionItems = () => {
    const { openItems } = state;

    // Map the registered items to an array of objects indicating their `id` and open status.
    return registeredItems.map((id) => ({ id, open: openItems.includes(id) }));
  };

  return [Object.freeze({ ...state }), {
    toggleItem, openItem, registerItem, getAccordionItems,
  }];
};

export default useAccordion;
